import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

const SITE = 'House Sharing Seniors';
const BASE = 'https://housesharingseniors.com.au';

const PAGE_META = {
  '/':                  { title: `Shared Housing for Age Pensioners Australia | ${SITE}`, desc: 'Save 40-60% on rent through safe vetted shared housing for Australian Age Pensioners. Browse 1400+ premium properties across Australia.', index: true },
  '/properties':        { title: `Browse Shared Housing Properties | ${SITE}`, desc: 'Browse 1400+ premium shared housing properties across Australia. Filter by state, suburb, bedrooms and rent. Ideal for Age Pensioners.', index: true },
  '/features':          { title: `HSS Platform Features | ${SITE}`, desc: 'Discover all features of the House Sharing Seniors platform — property discovery, CRA calculator, member matching and group formation.', index: true },
  '/faq':               { title: `FAQs | Shared Housing for Age Pensioners | ${SITE}`, desc: 'Answers to common questions about shared housing for Australian Age Pensioners — CRA eligibility, how it works, costs and safety.', index: true },
  '/rent-calculator':   { title: `CRA Rent Calculator | Commonwealth Rent Assistance | ${SITE}`, desc: 'Calculate your net rent after CRA for shared housing. See exactly how much Age Pensioners save with house sharing.', index: true },
  '/about':             { title: `About ${SITE}`, desc: 'House Sharing Seniors helps Australian Age Pensioners reduce housing costs by 40-60% through safe, vetted shared living.', index: true },
  '/contact':           { title: `Contact Us | ${SITE}`, desc: 'Contact House Sharing Seniors. Call 1300 77 5059. We help Australian Age Pensioners find affordable shared housing.', index: true },
  '/best-locations':    { title: `Best Locations for Age Pensioner Shared Housing | ${SITE}`, desc: 'Find the best Australian suburbs for Age Pensioner shared housing scored by walkability, transport and affordability.', index: true },
  '/investors':         { title: `Invest in Age Pensioner Housing | ${SITE}`, desc: 'High social return investment in Australian Age Pensioner shared housing. SROI up to $242:1. Government and NGO partnerships.', index: true },
  '/bond-loan':         { title: `Free Rental Bond Loans | ${SITE}`, desc: 'Free rental bond loans and financial assistance for Australian Age Pensioners moving into shared housing.', index: true },
  '/rea':               { title: `For Real Estate Agents | ${SITE}`, desc: 'Information for real estate agents listing properties for Age Pensioner shared housing through House Sharing Seniors.', index: true },
  '/testimonials':      { title: `Member Stories | ${SITE}`, desc: 'Stories from Australian Age Pensioners who found affordable shared housing through House Sharing Seniors.', index: true },
  '/privacy':           { title: `Privacy Policy | ${SITE}`, desc: 'House Sharing Seniors privacy policy — how we collect, use and protect your personal information.', index: true },
  '/terms':             { title: `Terms of Service | ${SITE}`, desc: 'House Sharing Seniors terms of service and conditions of use for members, visitors and property partners.', index: true },
  '/support':           { title: `Help & Support | ${SITE}`, desc: 'Get help from House Sharing Seniors. Find answers or contact our team about Age Pensioner shared housing.', index: true },
  '/resources':         { title: `Resources | ${SITE}`, desc: 'Resources for Australian Age Pensioners — CRA rent calculators, bond loan guides and housing assistance.', index: true },
  '/apply':             { title: `Apply for HSS Membership | ${SITE}`, desc: 'Apply to become a House Sharing Seniors member and access vetted shared housing. First 100 applications just $5.', index: true },
  '/login':             { title: `Member Login | ${SITE}`, desc: 'Log in to your House Sharing Seniors member account.', index: false },
  '/dashboard':         { title: `Member Dashboard | ${SITE}`, desc: 'Your HSS member dashboard.', index: false },
  '/admin':             { title: `Admin Dashboard | ${SITE}`, desc: 'HSS admin dashboard.', index: false },
  '/apply/form':        { title: `Membership Application | ${SITE}`, desc: 'Complete your HSS membership application.', index: false },
  '/payment':           { title: `Membership Payment | ${SITE}`, desc: 'Complete your HSS membership payment.', index: false },
  '/shared-housing/gold-coast':     { title: `Gold Coast Shared Housing for Age Pensioners | ${SITE}`, desc: 'Affordable shared housing on the Gold Coast for Australian Age Pensioners. Save 40-60% on rent. Browse local properties and apply today.', index: true },
  '/shared-housing/sunshine-coast': { title: `Sunshine Coast Shared Housing for Age Pensioners | ${SITE}`, desc: 'Affordable shared housing on the Sunshine Coast for Age Pensioners. Vetted co-living near beaches — save up to 60% on rent.', index: true },
  '/shared-housing/sydney':         { title: `Sydney Shared Housing for Age Pensioners | ${SITE}`, desc: 'Affordable shared housing in Sydney for Age Pensioners. Stay in the city you love while saving 40-60% on rent through vetted co-living.', index: true },
  '/shared-housing/melbourne':      { title: `Melbourne Shared Housing for Age Pensioners | ${SITE}`, desc: 'Affordable shared housing in Melbourne for Age Pensioners. Stay connected to Melbourne while saving 40-60% on rent through HSS.', index: true },
  '/shared-housing/brisbane':       { title: `Brisbane Shared Housing for Age Pensioners | ${SITE}`, desc: 'Affordable shared housing in Brisbane for Age Pensioners. Close to family and Queensland healthcare — save 40-60% on rent.', index: true },
};

function MetaUpdater() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    // Match exact or prefix (for /properties/:id)
    const meta = PAGE_META[path] || (path.startsWith('/properties/') ? {
      title: `Property Detail | ${SITE}`,
      desc: 'Shared housing property detail — Age Pensioner accommodation available across Australia.',
      index: true
    } : { title: SITE, desc: 'Safe affordable shared housing for Australian Age Pensioners.', index: true });

    document.title = meta.title;

    const setMeta = (name, content, prop = false) => {
      let el = prop
        ? document.querySelector(`meta[property="${name}"]`)
        : document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); prop ? el.setAttribute('property', name) : el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', meta.desc);
    setMeta('robots', meta.index ? 'index, follow' : 'noindex, nofollow');
    setMeta('og:title', meta.title, true);
    setMeta('og:description', meta.desc, true);
    setMeta('og:url', `${BASE}${path}`, true);
    setMeta('twitter:title', meta.title, true);
    setMeta('twitter:description', meta.desc, true);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${BASE}${path}`);

    // Robots meta: noindex,nofollow for private pages (dashboard, admin, apply/form, payment, login)
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) { robots = document.createElement('meta'); robots.setAttribute('name', 'robots'); document.head.appendChild(robots); }
    robots.setAttribute('content', meta.index === false ? 'noindex,nofollow' : 'index,follow,max-image-preview:large');
  }, [location.pathname]);
  return null;
}

// Schema markup — FAQPage, BreadcrumbList, HowTo
function SchemaUpdater() {
  const location = useLocation();

  useEffect(() => {
    // Remove previous page schema
    document.querySelectorAll('script.page-schema').forEach(s => s.remove());

    const path = location.pathname;
    const base = 'https://housesharingseniors.com.au';

    const inject = (schema) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.className = 'page-schema';
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
    };

    // BreadcrumbList on all non-home pages
    if (path !== '/' && !path.startsWith('/dashboard') && !path.startsWith('/admin') && !path.startsWith('/payment')) {
      const parts = path.split('/').filter(Boolean);
      const items = [{ "@type": "ListItem", "position": 1, "name": "Home", "item": base }];
      const names = {
        'properties': 'Properties', 'faq': 'FAQ', 'features': 'Features',
        'rent-calculator': 'Rent Calculator', 'about': 'About', 'contact': 'Contact',
        'best-locations': 'Best Locations', 'investors': 'Investors', 'bond-loan': 'Bond Loan',
        'rea': 'Real Estate Agents', 'testimonials': 'Testimonials', 'privacy': 'Privacy Policy',
        'terms': 'Terms', 'support': 'Support', 'resources': 'Resources',
        'apply': 'Apply', 'login': 'Login',
      };
      parts.forEach((part, i) => {
        const url = base + '/' + parts.slice(0, i + 1).join('/');
        items.push({ "@type": "ListItem", "position": i + 2, "name": names[part] || part, "item": url });
      });
      inject({ "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": items });
    }

    // FAQPage schema
    if (path === '/faq') {
      inject({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "How does house sharing work for age pensioners?", "acceptedAnswer": { "@type": "Answer", "text": "House Sharing Seniors matches Australian Age Pensioners who want to share a premium property with compatible housemates. Members apply online, complete our vetting process (identity verification, police check and references), then browse compatible housemates and properties across Australia. Costs — typically $150–$280 per bedroom per week — are divided equally. The arrangement reduces rent by 40–60% compared to renting alone, and all housemates remain eligible for Commonwealth Rent Assistance through Centrelink." } },
          { "@type": "Question", "name": "Can age pensioners share a house and still get Commonwealth Rent Assistance?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Australian Age Pensioners in shared housing are eligible for Commonwealth Rent Assistance (CRA) provided the rent is above the threshold. For singles the threshold is $74.10/wk and maximum CRA is $73.23/wk. For couples the threshold is $123.10/wk and maximum CRA is $103.40/wk." } },
          { "@type": "Question", "name": "What is Commonwealth Rent Assistance and how much can I get?", "acceptedAnswer": { "@type": "Answer", "text": "Commonwealth Rent Assistance (CRA) is a payment from Centrelink for Age Pensioners who pay rent privately. In shared housing, a single pensioner paying $250/wk per bedroom receives approximately $73.23/wk CRA, reducing net rent to around $177/wk. Couples receive up to $103.40/wk combined." } },
          { "@type": "Question", "name": "How much does house sharing save a pensioner?", "acceptedAnswer": { "@type": "Answer", "text": "House Sharing Seniors members typically pay $150–$280 per bedroom per week, compared to $300–$500+ for a solo rental. After Commonwealth Rent Assistance, a single pensioner's effective rent can be as low as $80–$180 per week — saving $150–$300 per week or over $10,000 per year." } },
          { "@type": "Question", "name": "Is HSS a government programme?", "acceptedAnswer": { "@type": "Answer", "text": "No. House Sharing Seniors is an independent private platform. However, members can still claim Commonwealth Rent Assistance (a government payment) while using HSS, as long as they meet the standard eligibility criteria for CRA through Centrelink." } },
          { "@type": "Question", "name": "Who is eligible to join House Sharing Seniors?", "acceptedAnswer": { "@type": "Answer", "text": "House Sharing Seniors is for Australian Age Pensioners seeking shared housing. Applicants must be Australian residents, complete an identity and police check, and commit to respectful co-living. The application includes an 8-step form covering lifestyle, preferences, and financial circumstances." } }
        ]
      });
    }

    // HowTo schema on features/about page
    if (path === '/features' || path === '/about') {
      inject({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Find Shared Housing as an Age Pensioner with House Sharing Seniors",
        "description": "A step-by-step guide for Australian Age Pensioners to find affordable shared housing through House Sharing Seniors.",
        "step": [
          { "@type": "HowToStep", "position": 1, "name": "Apply for Membership", "text": "Complete the HSS membership application. It takes 10–15 minutes and covers your lifestyle, preferences and circumstances." },
          { "@type": "HowToStep", "position": 2, "name": "Get Approved", "text": "Our team reviews your application within a few days. We verify your identity, police check and references." },
          { "@type": "HowToStep", "position": 3, "name": "Browse Properties & Members", "text": "Once approved, browse 1400+ premium properties and compatible member profiles across Australia." },
          { "@type": "HowToStep", "position": 4, "name": "Form a Group & Move In", "text": "Connect with compatible housemates, form a group for a specific property, and arrange to move in together." }
        ]
      });
    }
  }, [location.pathname]);

  return null;
}

function OrgSchema() {
  const location = useLocation();
  useEffect(() => {
    const existing = document.getElementById('org-schema');
    if (existing) existing.remove();
    if (location.pathname === '/') {
      const s = document.createElement('script');
      s.id = 'org-schema';
      s.type = 'application/ld+json';
      s.text = JSON.stringify({
        "@context": "https://schema.org", "@type": "Organization",
        "name": SITE, "url": BASE,
        "logo": `${BASE}/logo.png`,
        "description": "Safe, affordable shared housing for Australian Age Pensioners. Reduce rent by 40-60%.",
        "telephone": "1300775059",
        "email": "newmembers@housesharingseniors.com.au",
        "address": { "@type": "PostalAddress", "addressCountry": "AU" }
      });
      document.head.appendChild(s);
    }
  }, [location.pathname]);
  return null;
}

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import StartApplication from './pages/StartApplication';
import ResumeApplication from './pages/ResumeApplication';
import ApplicationFormNew from './pages/ApplicationFormNew';
import AddProperty from './pages/AddProperty';
import EditProperty from './pages/EditProperty';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import AdminDashboard from './pages/AdminDashboard';
import MemberDashboard from './pages/MemberDashboard';
import MemberProfilePage from './pages/MemberProfilePage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
// Static Pages
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePage from './pages/CookiePage';
import TermsPage from './pages/TermsPage';
import FAQPage from './pages/FAQPage';
import FeaturesPage from './pages/FeaturesPage';
import InvestorsPage from './pages/InvestorsPage';
import PaymentPage from './pages/PaymentPage';
import RentCalculatorPage from './pages/RentCalculatorPage';
import BestLocationsPage from './pages/BestLocationsPage';
import BondLoanPage from './pages/BondLoanPage';
import TestimonialsPage from './pages/TestimonialsPage';
import REAPage from './pages/REAPage';
import SupportPage from './pages/SupportPage';
import ResourcesPage from './pages/ResourcesPage';
import SitemapPage from './pages/SitemapPage';
import NotFoundPage from './pages/NotFoundPage';
import { GoldCoastPage, SunshineCoastPage, SydneyPage, MelbournePage, BrisbanePage } from './pages/CityPages';
import './App.css';

function App() {
  return (
    <Router>
      <MetaUpdater />
      <OrgSchema />
      <SchemaUpdater />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Navigate to="/login?register=true" replace />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/apply" element={<StartApplication />} />
        <Route path="/apply/resume" element={<ResumeApplication />} />
        <Route path="/apply/form" element={<ApplicationFormNew />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/properties/add" element={<ProtectedRoute><AddProperty /></ProtectedRoute>} />
        <Route path="/properties/:propertyId" element={<PropertyDetailPage />} />
        <Route path="/properties/:propertyId/edit" element={<ProtectedRoute><EditProperty /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/*" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><MemberDashboard /></ProtectedRoute>} />
        <Route path="/members/:userId" element={<ProtectedRoute><MemberProfilePage /></ProtectedRoute>} />
        {/* Static Pages */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/investors" element={<InvestorsPage />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/payment/success" element={<PaymentPage />} />
        <Route path="/rent-calculator" element={<RentCalculatorPage />} />
        <Route path="/best-locations" element={<BestLocationsPage />} />
        <Route path="/bond-loan" element={<BondLoanPage />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
        <Route path="/rea" element={<REAPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/shared-housing/gold-coast" element={<GoldCoastPage />} />
        <Route path="/shared-housing/sunshine-coast" element={<SunshineCoastPage />} />
        <Route path="/shared-housing/sydney" element={<SydneyPage />} />
        <Route path="/shared-housing/melbourne" element={<MelbournePage />} />
        <Route path="/shared-housing/brisbane" element={<BrisbanePage />} />
        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

export default App;
